// The share card (sprint 28 item 2, roadmap 59): a one-bit picture made from the reveal card, in two sizes, for every end
// screen. plan -> layout (plain data, testable without a canvas) -> draw. Ink and paper; the portrait keeps its accents (an
// alien's red eyes, the infected's green); gold only on the gilded envelope card. Text is never an accent: it is ink or paper
// by coverage (thresholdRegion), like the game's own pixel text.
// Privacy: a card carries our punchline, the player's score, an optional name and a line only when the player's own or an
// AI's (a knocker's quoted line is marked quoteBy 'human' and left out). Never a wallet, SOL, a price or a prize.
import { ALLOWED, ACCENTS, INK, PAPER, PALETTE, createBits } from './bits.js';
import { mugshot, CARD_TITLE } from './reveal-card.js';
import { isCardSafe, pickPunchline } from './punchlines.js';
import { shareText } from '../game/bigknock-copy.js';
import { opened, mark, grid as mgrid, lit, MARK, INK as M_INK, PAPER as M_PAPER, GOLD as M_GOLD } from './mail-art.js';
import { headArt, headFrame, drawnAnimal } from './animal-art.js';
import { skinSheet, SHEET_W, SHEET_H } from './knocker.js';

export const SIZES = { story: { w: 1080, h: 1920 }, square: { w: 1080, h: 1080 } };
export const MIN_TEXT_PX = 40; // 14 px at 2x is the art bible's floor; on a 390 px phone a 1080 px card shows at about 0.36, so 40 px
export { SHARE_SCREENS as SCREENS } from '../game/share.js';
export const SHARE_EVENT = 'share'; // the message ui.js sends per click ({ t, screen }); counted on /admin/measurements
export const CARD_PALETTE = ALLOWED;
export const CARD_FONT = '"Atkinson Hyperlegible", Verdana, sans-serif';
const BOTS = new Set(['alien', 'infected']);
const KINDS = new Set(['alien', 'infected', 'human']);

// What happened, from the player's side (the punchline bank's keys).
export function outcomeOf({ screen = 'reveal', verdict, kind, mail = null }) {
  if (screen === 'firstMinute') return 'firstMinute';
  if (screen === 'practice') { // told from the call that was made; with no call to judge, a line that claims neither (sprint 30)
    if ((verdict !== 'open' && verdict !== 'shut') || !KINDS.has(kind)) return 'practiceEnd';
    return (verdict === 'open') !== BOTS.has(kind) ? 'practiceRight' : 'practiceWrong';
  }
  if (screen === 'shift') return 'shiftEnd';
  if (verdict === 'timeout') return 'timeout';
  if (mail) return 'envelope'; // a right call that dropped an envelope (any tier): the envelope bank
  const bot = BOTS.has(kind);
  return verdict === 'open' ? (bot ? 'wrongOpen' : 'rightOpen') : bot ? 'rightShut' : 'wrongShut';
}

const num = n => Number.isFinite(n) ? Math.round(n) : null;
export function scoreLineOf({ screen = 'reveal', points, streak, shiftTotal }) {
  if (screen === 'shift' && num(shiftTotal) !== null) return `Shift total: ${num(shiftTotal)} points`;
  if (num(streak) >= 2) return `${num(streak)} right in a row`;
  const p = num(points);
  return p === null ? null : `${p > 0 ? '+' : ''}${p} points`;
}

// A name or a line is kept only when it is plain: no money or wallet talk, nothing long, no run of digits.
export function safeText(s, max) {
  const t = String(s ?? '').replace(/\s+/g, ' ').trim();
  if (!t || t.length > max || !isCardSafe(t) || /\d{5,}/.test(t)) return null;
  return t;
}

// Who spoke the quoted line. In this game "visitor" means an alien, so a neighbour is never called one.
export function saidLabel({ kind, quoteBy }) {
  if (quoteBy === 'self') return 'I said:';
  if (kind === 'alien') return 'The visitor said:';
  if (kind === 'infected') return 'They said:';
  return 'The neighbour said:';
}

// quoteBy: 'ai' | 'self' are kept, 'human' (a knocker typed it) is left out, anything unknown is left out too.
// The Big Knock's card (sprint 29 item 3): the winner gets the big envelope card, a right call the points one, a fooled player the plain one with the portrait.
// Words from game/bigknock-copy.js shareText: counts of people only, never a wallet, points or fragment amounts. A name only when the player chose one.
export function bigknockPlan({ n, outcome, right = 0, real = 0, kind = null, name = '', host = '' }) {
  const t = shareText(n, outcome, { right, real }), won = outcome === 'winner', pts = outcome === 'right';
  return { screen: 'bigknock', outcome: won || pts ? 'envelope' : 'wrongOpen', punchline: t.head, kind: won || pts ? null : KINDS.has(kind) ? kind : null, mail: won ? 'big' : pts ? 'points' : null,
    tier: null, quote: null, said: saidLabel({ kind, quoteBy: null }), score: t.sub || null, name: safeText(name, 20),
    still: pts && KINDS.has(kind) ? kind : null, // a right call's card shows who it was (the reveal card's portrait) beside its envelope
    brand: 'KNOCK', link: String(host || '').replace(/^https?:\/\//, '').replace(/\/$/, '') || null };
}

export function cardPlan({ screen = 'reveal', verdict, kind, mail = null, quote = null, quoteBy = null, points, streak, shiftTotal, name = '', host = '', last = null, rand, bigknock = null, doors = [], room = null, night = null, met = null }) {
  if (bigknock) return bigknockPlan({ ...bigknock, name, host });
  const outcome = outcomeOf({ screen, verdict, kind, mail });
  const line = pickPunchline(outcome, { last, rand });
  return {
    screen, outcome, punchline: line, kind: KINDS.has(kind) ? kind : null, mail: outcome === 'envelope' ? mail : null,
    tier: outcome === 'envelope' && mail === 'jackpot' ? 'Gilded.' : outcome === 'envelope' && mail === 'big' ? 'Big.' : null, // the tier word on its own line; never an amount
    quote: quoteBy === 'ai' || quoteBy === 'self' ? safeText(quote, 160) : null,
    said: saidLabel({ kind, quoteBy }),
    score: scoreLineOf({ screen, points, streak, shiftTotal }),
    name: safeText(name, 20),
    brand: 'KNOCK', link: String(host || '').replace(/^https?:\/\//, '').replace(/\/$/, '') || null,
    met: met?.key && met?.name ? { key: met.key, name: safeText(met.name, 40), count: met.count, of: met.of } : null, // a first meeting at this door (game/cast.js)
    ...tallyOf(doors, room, night),
  };
}

// The doors the player has just kept (sprint 34 item 3 fix): one {verdict, kind, name} per door, as the reveal reported it. The list resets
// when the key changes (a new night shift, or another room) and keeps the last DOOR_KEEP. A timeout is not a door.
export const DOOR_KEEP = 10;
export function recordDoor(store, door, key) {
  if (door?.verdict !== 'open' && door?.verdict !== 'shut') return store;
  if (store.key !== key) { store.key = key; store.list = []; }
  store.list.push({ verdict: door.verdict, kind: door.kind, name: door.name ?? '' });
  if (store.list.length > DOOR_KEEP) store.list.splice(0, store.list.length - DOOR_KEEP);
  return store;
}
// Which of the artist's door icons a door gets. A person is kind 'human'; the infected and the visitors are not.
export function iconOf({ verdict, kind, name = '' }) {
  if (kind === 'human') return verdict === 'open' ? (/^noor\b/i.test(name) ? 'welcomed-noor' : 'welcomed-rosa') : 'left';
  return verdict === 'open' ? 'gotin' : 'kept';
}
// The night's tally for the card. The shift data carries no night number, so "night N" shows only if a caller has one; the free rooms say "practice".
function tallyOf(doors, room, night) {
  const icons = (doors ?? []).slice(-DOOR_KEEP).map(iconOf);
  return { doors: icons, kept: icons.filter(k => k === 'kept').length, gotIn: icons.filter(k => k === 'gotin').length,
    night: Number.isInteger(night) && night > 0 ? `night ${night}` : room === 'practice' ? 'practice' : null };
}

// The ready post for X: our line, the score, the link. Under 280 characters.
export function xPostText(plan) {
  return [plan.punchline, plan.met ? `${plan.met.name} knocked at my door.` : null, plan.score, plan.link ? `Can you tell who is at the door? ${plan.link}` : 'Can you tell who is at the door?'].filter(Boolean).join(' ').slice(0, 280);
}
export const cardAlt = plan => [plan.punchline, plan.quote ? `${plan.said ?? 'They said:'} ${plan.quote}` : '', plan.score, plan.brand].filter(Boolean).join(' ');

// ---- art: cells -> runs of whole-pixel rects ----
// The reveal card's own portrait (reveal-card.js mugshot, 28 x 34), a cell at a time.
const PIC = { w: 28, h: 34 };
// The character met at this door (game/cast.js), for the card's window: a drawn head on the person's coat, ink and paper, or a
// scream's stand-in with its red eyes. A key with no drawing (a story neighbour, a knock, a game) keeps the reveal's portrait.
const MET = { w: 64, h: 76 };
export function metCells(key) {
  const [group, id] = String(key).split(':');
  if (['regular', 'degen', 'animal'].includes(group) && drawnAnimal(id)) {
    const art = headArt(id, headFrame(id, 0, 0)), cells = new Map();
    if (!art) return null;
    for (let y = 60; y < MET.h; y++) for (let x = 0; x < MET.w; x++) if (((x - 32) / 26) ** 2 + ((y - 78) / 16) ** 2 <= 1) cells.set(y * MET.w + x, INK); // the coat
    for (let k = 0; k < art.px.length; k += 3) {
      const x = art.px[k] >> 1, y = art.px[k + 1] >> 1;
      if (x < MET.w && y < MET.h) cells.set(y * MET.w + x, art.px[k + 2] <= 1 ? INK : PAPER);
    }
    return { ...MET, at: (i, j) => cells.get(j * MET.w + i) ?? PAPER };
  }
  if (group === 'fail' && skinSheet(id)) {
    const k = skinSheet(id), body = k.body(k.frames - 1), s = SHEET_W / MET.w;
    const at = (i, j) => { const c = body[Math.min(SHEET_H - 1, Math.floor(j * s)) * SHEET_W + Math.floor(i * s)]; return c === 3 ? ACCENTS.red : c === 2 ? PAPER : INK; };
    return { ...MET, at };
  }
  return null;
}
function mugshotCells(kind) {
  const B = createBits(PIC.w, PIC.h); mugshot(B, 0, 0, kind);
  return { ...PIC, at: (i, j) => (B.bits[j * PIC.w + i] ? PALETTE[B.tint[j * PIC.w + i]] : INK) };
}
// The envelope (sprint 29 item 2): the reveal's own art from mail-art.js, the opened envelope with its letter half out, in the light
// of its tier, filling the window like the game's "Under the door" window. Every tier gets the same large, heavy envelope (a paper
// then an ink rim round it) and its burst: points, the lamp's dithered pool with thin paper rays; big, paper rays and ink wedges to
// the edges and the big mark stamped at twice the size; gilded, the same in gold with paper sparkles and the gold medal. Ink and paper,
// gold only on gilded. ENVELOPE_ART: the art's size in cells, tall card and square card.
export const ENVELOPE_ART = { tall: { w: 76, h: 48 }, square: { w: 60, h: 48 } };
const MAIL_FILL = { [M_INK]: INK, [M_PAPER]: PAPER, [M_GOLD]: ACCENTS.gold };
export function envelopeArt(tier, w, h) {
  const gold = tier === 'jackpot', big = tier === 'big' || gold, d = mgrid(w, h);
  const env = opened(gold ? 'jackpot' : big ? 'big' : tier === 'shield' ? 'shield' : 'points'), ex = Math.round((w - env.w) / 2), ey = h - env.h - 3;
  const cx = ex + env.w / 2, cy = ey + env.h / 2, N = 24;
  d.each((x, y) => {
    const dx = x + 0.5 - cx, dy = y + 0.5 - cy, r = Math.hypot(dx, dy * 1.15), a = Math.atan2(dy, dx), u = (a + Math.PI) / (2 * Math.PI / N), k = Math.floor(u) % N;
    if (!big) { // the lamp's pool, then thin rays running out of it
      if (r > 27 && u % 1 < 0.22 && k % 2 === 0) return M_PAPER;
      return lit(x, y, 0.06 + 0.9 * Math.max(0, 1 - r / 34)) ? M_PAPER : M_INK;
    }
    if (r < 8) return gold ? M_GOLD : M_PAPER;
    return k % 3 !== 2 ? (gold ? M_GOLD : M_PAPER) : M_INK; // two lit rays to one ink wedge, out to the edges
  });
  const stamp = (s, ox, oy, k = 1) => { // a sprite at k cells per art px, ringed in paper then ink so it reads on any light
    const on = (x, y) => { const i = Math.floor((x - ox) / k), j = Math.floor((y - oy) / k); return i >= 0 && j >= 0 && i < s.w && j < s.h && s.get(i, j) ? s.get(i, j) : 0; };
    const near = (x, y, n) => { for (let j = -n; j <= n; j++) for (let i = -n; i <= n; i++) if (on(x + i, y + j)) return true; return false; };
    d.each((x, y) => (on(x, y) ? on(x, y) : near(x, y, 1) ? M_PAPER : near(x, y, 2) ? M_INK : undefined));
  };
  stamp(env, ex, ey);
  if (big) stamp(mark(gold ? 'gilded' : 'big'), Math.min(w - 2 * MARK - 3, ex + env.w - 6), 3, 2);
  if (gold) for (const [x, y] of [[5, 6], [w - 7, h - 8], [8, h - 10], [w - 10, 18], [16, 3]]) {
    for (const [i, j] of [[-2, 0], [2, 0], [0, -2], [0, 2], [-1, -1], [1, -1], [-1, 1], [1, 1]]) d.set(x + i, y + j, M_INK);
    for (const [i, j] of [[0, 0], [-1, 0], [1, 0], [0, -1], [0, 1]]) d.set(x + i, y + j, M_PAPER); // a paper sparkle in an ink halo
  }
  return { w, h, at: (i, j) => MAIL_FILL[d.get(i, j)] ?? null };
}
export const ENVELOPE_TITLE = { points: 'An envelope', shield: 'An envelope', big: 'A big envelope', jackpot: 'A gilded envelope' };
const envelopeCells = (tier, tall) => { const s = ENVELOPE_ART[tall ? 'tall' : 'square']; return envelopeArt(tier, s.w, s.h); };
// The right call's hero (sprint 29 fix pass, art): the visitor's still (the reveal card's portrait, 28 x 34) in an ink panel with a paper rim, left of
// the envelope's art, same height. STILL_W: the panel's width in cells. Ink and paper; the portrait keeps its own accents (an alien's red eyes).
export const STILL_W = 34;
export function withStill(env, kind) {
  const pic = mugshotCells(kind), px = 3, py = Math.floor((env.h - pic.h) / 2);
  const at = (i, j) => {
    if (i >= STILL_W) return env.at(i - STILL_W, j);
    const u = i - px, v = j - py;
    if (u >= 0 && v >= 0 && u < pic.w && v < pic.h) return pic.at(u, v);
    if (u >= -1 && v >= -1 && u <= pic.w && v <= pic.h) return PAPER; // the rim
    return i === STILL_W - 1 ? PAPER : INK; // a paper rule between the still and the envelope
  };
  return { w: env.w + STILL_W, h: env.h, at };
}
// rects for a cell grid at whole-pixel scale s, merged along each row
function cellRects(art, x, y, s) {
  const out = [];
  for (let j = 0; j < art.h; j++) {
    for (let i = 0; i < art.w;) {
      const c = art.at(i, j); let n = 1;
      if (!c) { i++; continue; }
      while (i + n < art.w && art.at(i + n, j) === c) n++;
      out.push({ x: x + i * s, y: y + j * s, w: n * s, h: s, fill: c }); i += n;
    }
  }
  return out;
}

// ---- layout: rects and texts in canvas pixels. measure(text, px, bold) -> width ----
const fontOf = (px, bold) => `${bold ? '700 ' : ''}${px}px ${CARD_FONT}`;
export const fontFor = fontOf;
export function wrapText(text, maxW, px, bold, measure) {
  const lines = []; let cur = '';
  for (const w of String(text).split(' ')) {
    const next = cur ? `${cur} ${w}` : w;
    if (cur && measure(next, px, bold) > maxW) { lines.push(cur); cur = w; } else cur = next;
  }
  if (cur) lines.push(cur);
  return lines;
}

// A window in the game's look: an ink frame, a striped title bar with the title on a paper gap.
function windowRects(rects, texts, measure, x, y, w, h, bar, title, tPx, textFn) {
  rects.push({ x, y, w, h, fill: INK }, { x: x + 6, y: y + 6, w: w - 12, h: h - 12, fill: PAPER });
  for (let j = y + 12; j + 5 <= y + bar - 10; j += 12) rects.push({ x: x + 6, y: j, w: w - 12, h: 5, fill: INK });
  rects.push({ x: x + 6, y: y + bar - 6, w: w - 12, h: 6, fill: INK });
  if (title) {
    const tw = Math.ceil(measure(title, tPx, true)) + 40;
    rects.push({ x: Math.round(x + w / 2 - tw / 2), y: y + 8, w: tw, h: bar - 16, fill: PAPER });
    textFn(title, x + w / 2, y + bar / 2 + Math.round(tPx * 0.34), tPx, true, INK, 'center');
  }
}

export function layoutShareCard(plan, size, measure) {
  const { w, h } = size, tall = h > w, M = 84, W = w - M * 2;
  const rects = [], texts = [];
  const text = (t, x, y, px, bold = false, fill = INK, align = 'left') => texts.push({ text: t, x, y, px, bold, fill, align, font: fontOf(px, bold) });
  rects.push({ x: 0, y: 0, w, h, fill: PAPER });
  rects.push({ x: 24, y: 24, w: w - 48, h: h - 48, fill: INK }, { x: 36, y: 36, w: w - 72, h: h - 72, fill: PAPER });
  // the banner: a striped title bar like the game's windows, KNOCK in ink on a paper gap
  const bannerH = tall ? 150 : 120, bannerY = 36, bPx = tall ? 88 : 72;
  for (let j = bannerY + 14; j + 6 <= bannerY + bannerH - 14; j += 12) rects.push({ x: 36, y: j, w: w - 72, h: 6, fill: INK });
  const kw = Math.ceil(measure(plan.brand, bPx, true)) + 80;
  rects.push({ x: Math.round(w / 2 - kw / 2), y: bannerY + 8, w: kw, h: bannerH - 16, fill: PAPER });
  rects.push({ x: 36, y: bannerY + bannerH, w: w - 72, h: 8, fill: INK });
  text(plan.brand, w / 2, bannerY + bannerH / 2 + (tall ? 28 : 22), bPx, true, INK, 'center');
  // the footer: the link
  const footPx = 52, footY = h - 36 - (tall ? 110 : 86);
  rects.push({ x: 36, y: footY - 40, w: w - 72, h: 8, fill: INK });
  text(plan.link ?? 'Can you tell who is at the door?', w / 2, footY + 36, footPx, true, INK, 'center');
  // the hero: the envelope's art on an envelope card, else the reveal card's portrait
  const metArt = plan.outcome !== 'envelope' && plan.met ? metCells(plan.met.key) : null;
  const envelope = plan.outcome === 'envelope', art = envelope ? (plan.still ? withStill(envelopeCells(plan.mail, tall), plan.still) : envelopeCells(plan.mail, tall)) : metArt ?? (plan.kind ? mugshotCells(plan.kind) : null);
  const heroTitle = envelope ? ENVELOPE_TITLE[plan.mail] ?? ENVELOPE_TITLE.points : plan.met ? plan.met.name : CARD_TITLE;
  // the envelope's art fills its window edge to edge (inside the 6 px frame); the portrait sits on paper in a 600 wide window
  const heroBox = s => {
    const bar = tall ? 64 : 30, pad = envelope ? 0 : tall ? 28 : 16, hw = envelope ? art.w * s + 12 : tall ? 600 : art.w * s + pad * 2;
    return { s, bar, pad, w: hw, h: bar + art.h * s + pad * 2 + (envelope ? 6 : 0) };
  };
  const scales = envelope ? (tall ? [7, 6, 5, 4] : [5, 4, 3]) : tall ? [8, 6, 4] : [5, 4, 3];
  // the body, laid out top down (the hero above it on the tall card, left of the punchline on the square one), then centred in the room
  const top = bannerY + bannerH + 8 + (tall ? 60 : 40), bottom = footY - 40 - (tall ? 60 : 40), room = bottom - top;
  const build = (withQuote, s) => {
    const hb = art ? heroBox(s) : null, side = hb && !tall ? hb.w + 40 : 0, pW = W - side;
    const items = []; let y = hb && tall ? hb.h + 50 : 0;
    const pPx = tall ? 108 : 92, pLines = wrapText(plan.punchline, pW, pPx, true, measure).slice(0, 5);
    for (const l of pLines) { items.push({ t: l, px: pPx, bold: true, y: y + pPx, x: M + side }); y += Math.round(pPx * 1.2); }
    if (plan.tier) { items.push({ t: plan.tier, px: pPx, bold: true, y: y + pPx, x: M + side, gold: plan.tier === 'Gilded.' }); y += Math.round(pPx * 1.2); }
    if (hb && !tall) y = Math.max(y, hb.h);
    y += tall ? 70 : 36;
    let barFrom = null, barTo = null;
    if (withQuote && plan.quote) {
      const qPx = tall ? 56 : 48, qLines = wrapText(`“${plan.quote}”`, W - 40, qPx, false, measure);
      const cap = qLines.slice(0, tall ? 5 : 3);
      if (cap.length < qLines.length) cap[cap.length - 1] = cap[cap.length - 1].replace(/\s*\S*$/, '') + '…”';
      barFrom = y;
      items.push({ t: plan.said ?? 'They said:', px: MIN_TEXT_PX + 4, bold: true, y: y + MIN_TEXT_PX + 4, x: M + 40 }); y += Math.round((MIN_TEXT_PX + 4) * 1.5);
      for (const l of cap) { items.push({ t: l, px: qPx, bold: false, y: y + qPx, x: M + 40 }); y += Math.round(qPx * 1.3); }
      barTo = y; y += tall ? 70 : 36;
    }
    if (plan.score) { items.push({ t: plan.score, px: tall ? 72 : 64, bold: true, y: y + (tall ? 72 : 64), x: M }); y += Math.round((tall ? 72 : 64) * 1.3); }
    if (plan.met) { // who it was (the tall card's window bar names them already), then the logbook's count
      if (!tall) { items.push({ t: plan.met.name, px: 56, bold: true, y: y + 56 + 10, x: M }); y += 56 + 30; }
      items.push({ t: `New in my logbook: ${plan.met.count} of ${plan.met.of}`, px: 48, bold: false, y: y + 48 + 10, x: M }); y += 48 + 28;
    }
    if (plan.name) { items.push({ t: `Doorkeeper: ${plan.name}`, px: 48, bold: false, y: y + 48 + 10, x: M }); y += 48 + 28; }
    return { items, height: y, barFrom, barTo, hb };
  };
  // the room gives way: the hero shrinks, then the quote goes; on an envelope card the art is the point, so at each size the quote goes first
  const tries = envelope ? scales.flatMap(s => [[true, s], [false, s]]) : [true, false].flatMap(q => (art ? scales : [0]).map(s => [q, s]));
  let body = null;
  for (const [q, s] of tries) { const b = build(q, s); if (b.height <= room) { body = b; break; } }
  body ??= build(false, tries.at(-1)[1]);
  const oy = top + Math.max(0, Math.round((room - body.height) / 2));
  if (body.hb) {
    const hb = body.hb, hx = tall ? Math.round((w - hb.w) / 2) : M, hy = oy;
    windowRects(rects, texts, measure, hx, hy, hb.w, hb.h, hb.bar, tall ? heroTitle : null, 40, text);
    rects.push(...cellRects(art, Math.round(hx + (hb.w - art.w * hb.s) / 2), hy + hb.bar + hb.pad, hb.s));
  }
  if (body.barFrom !== null) rects.push({ x: M, y: oy + body.barFrom + 4, w: 10, h: body.barTo - body.barFrom - 12, fill: INK });
  for (const it of body.items) {
    if (it.gold) rects.push({ x: it.x - 16, y: oy + it.y - it.px, w: Math.round(measure(it.t, it.px, true)) + 32, h: Math.round(it.px * 1.2), fill: ACCENTS.gold }); // the gilded card's gold word box
    text(it.t, it.x, oy + it.y, it.px, it.bold);
  }
  return { w, h, rects, texts, fits: body.height <= room, heroScale: body.hb?.s ?? null, quoted: body.barFrom !== null };
}

// ---- the bright card (sprint 34 item 3): the approved B2 frame (Aseprite, drawn 1:1) with the card's words over it ----
// The frame and the door icons are pictures, never redrawn here: the dot paper, the KNOCK wordmark, the Knocker over the ledge, and the
// artist's door icons from the doors sheet, each copied 1:1 into the grid slice's slots. The game writes the words at screen resolution:
// "night N", the tally ("I kept N out." in ink, "N got in." in red, the card's only red text), the slip, the link and the disclaimer.
// The frame's art pixel is 6 card px, so every text size is a whole number of art pixels (a multiple of 6, floor 42 = 7); 40 is not one.
// The punchline, tier, score and name are not in the approved layout: they stay in the pasted post and on the old card (the fallback).
export const BRIGHT_SIZES = { story: { w: 1080, h: 1920 }, feed: { w: 1080, h: 1350 } };
export const BRIGHT_MIN_PX = 42;
export const BRIGHT_INK = '#2b1a47', BRIGHT_PLUM = '#4c3380', BRIGHT_LILAC = '#c8b6ff', BRIGHT_CREAM = '#fff1cf', BRIGHT_WHITE = '#fffdf6', BRIGHT_RED = '#e24034';
export const BRIGHT_PALETTE = [BRIGHT_INK, BRIGHT_PLUM, BRIGHT_LILAC, BRIGHT_CREAM, BRIGHT_WHITE, BRIGHT_RED];
export const CARD_DISCLAIMER = 'Not an investment. Prizes go to the best players of each night shift.';
const FRAME_DIR = '/art/aseprite/b2-share/';
const ART = 6; // card px per art px
// Slices from b2-share-*-frame-slices.json, in art px: the night slice, the first door slot (w, h) and the pitch to the next (dx, dy), the
// result and slip tops. A test checks the slots against the JSON. bottom: the cream above the Knocker's dome; ledge: where the ink ledge starts.
const FRAMES = {
  story: { file: FRAME_DIR + 'b2-share-story-frame-x6.png', sheet: FRAME_DIR + 'b2-share-story-doors-sheet-x6.png', bottom: 1140, ledge: 1758,
    night: { x: 105, y: 23, h: 9 }, slot: { x: 29, y: 44, w: 18, h: 26, dx: 26, dy: 34, perRow: 5 }, resultY: 112, slipY: 149 },
  feed: { file: FRAME_DIR + 'b2-share-feed-frame-x6.png', sheet: FRAME_DIR + 'b2-share-feed-doors-sheet-x6.png', bottom: 760, ledge: 1188,
    night: { x: 102, y: 15, h: 9 }, slot: { x: 11, y: 30, w: 13, h: 19, dx: 16, dy: 0, perRow: 10 }, resultY: 58, slipY: 93 },
};
const ICON_FRAMES = ['kept', 'welcomed-rosa', 'left', 'welcomed-noor', 'gotin']; // the sheet's frame order (its tags)
export const brightKind = size => (size.h >= BRIGHT_SIZES.story.h ? 'story' : 'feed');
export const brightFrameFile = size => FRAMES[brightKind(size)].file;
export const brightSheetFile = size => FRAMES[brightKind(size)].sheet;

export function layoutBrightCard(plan, size, measure) {
  const { w, h } = size, F = FRAMES[brightKind(size)], M = 72, W = w - M * 2;
  const rects = [], texts = [], icons = [];
  const text = (t, x, y, px, bold, fill, align = 'left') => texts.push({ text: t, x, y, px, bold, fill, align, font: fontOf(px, bold) });
  if (plan.night) text(plan.night, F.night.x * ART, (F.night.y + F.night.h) * ART - 6, BRIGHT_MIN_PX, true, BRIGHT_PLUM); // at the night slice's left edge
  const S = F.slot; // the door icons, one per slot, filled from the first
  (plan.doors ?? []).slice(0, 10).forEach((kind, i) => {
    const n = ICON_FRAMES.indexOf(kind);
    if (n >= 0) icons.push({ kind, sx: n * S.w * ART, sy: 0, sw: S.w * ART, sh: S.h * ART, x: (S.x + S.dx * (i % S.perRow)) * ART, y: (S.y + S.dy * Math.floor(i / S.perRow)) * ART });
  });
  if (icons.length) { // the tally, centred on the card, in the result slice
    const y0 = F.resultY * ART;
    text(`I kept ${plan.kept} out.`, w / 2, y0 + 60, 66, true, BRIGHT_INK, 'center');
    if (plan.gotIn > 0) text(`${plan.gotIn} got in.`, w / 2, y0 + 138, 66, true, BRIGHT_RED, 'center');
  }
  let slipped = false; // the slip: its label, then the quoted line in the ring; the biggest size that stays above the Knocker's dome
  if (plan.quote) {
    const top = F.slipY * ART, label = Math.round(BRIGHT_MIN_PX * 1.4);
    for (const [q, cap] of [[54, 3], [48, 3], [48, 2], [42, 2], [42, 1]]) {
      const lines = wrapText(`“${plan.quote}”`, W - 72, q, false, measure), shown = lines.slice(0, cap);
      if (shown.length < lines.length) shown[shown.length - 1] = shown[shown.length - 1].replace(/\s*\S*$/, '') + '…”';
      const lh = Math.round(q * 1.3), ph = 72 + (shown.length - 1) * lh + Math.round(q * 1.25), y = top + label;
      if (y + ph > F.bottom) continue;
      text(plan.said ?? 'They said:', M, top + BRIGHT_MIN_PX, BRIGHT_MIN_PX, true, BRIGHT_PLUM);
      rects.push({ x: M, y, w: W, h: ph, fill: BRIGHT_INK }, { x: M + 6, y: y + 6, w: W - 12, h: ph - 12, fill: BRIGHT_CREAM }, { x: M + 18, y: y + 18, w: W - 36, h: ph - 36, fill: BRIGHT_INK });
      shown.forEach((l, i) => text(l, M + 36, y + 36 + i * lh + q, q, false, BRIGHT_CREAM));
      slipped = true;
      break;
    }
  }
  // the ledge: the link, then the disclaimer, in the sentence font on the ink (the baked sample words are not in the frame)
  const ledgeH = h - F.ledge, linkPx = 54, dPx = BRIGHT_MIN_PX, dLines = CARD_DISCLAIMER.match(/[^.]+\./g).map(s => s.trim()); // a sentence a line, as the approved frame had it
  const dTop = h - 18 - (dLines.length - 1) * 48;
  text(plan.link ?? 'Can you tell who is at the door?', w / 2, F.ledge + 52, linkPx, true, BRIGHT_CREAM, 'center');
  dLines.forEach((l, i) => text(l, w / 2, dTop + i * 48, dPx, true, BRIGHT_LILAC, 'center'));
  return { w, h, rects, texts, icons, frame: F.file, sheet: F.sheet, fits: ledgeH >= 150, bright: true, quoted: slipped };
}

// ---- pixels ----
// Text is drawn like the game's: a pixel is the text colour where the glyph covers enough of it (25% regular, 50% bold),
// the background otherwise. Nothing is ever snapped to an accent. before/after: RGBA of the same region without and with
// the text; fill: [r, g, b]. Returns after, rewritten.
export function thresholdRegion(before, after, fill, thr) {
  for (let i = 0; i < after.length; i += 4) {
    let den = 0, num = 0;
    for (let m = 0; m < 3; m++) { den += Math.abs(fill[m] - before[i + m]); num += Math.abs(after[i + m] - before[i + m]); }
    const on = den > 0 && num / den >= thr;
    for (let m = 0; m < 3; m++) after[i + m] = on ? fill[m] : before[i + m];
    after[i + 3] = 255;
  }
  return after;
}
const rgbOf = c => [1, 3, 5].map(i => parseInt(c.slice(i, i + 2), 16));
const CARD_RGB = CARD_PALETTE.map(rgbOf);
// Nearest allowed colour for every pixel (kept for callers that want it; the card itself no longer needs it).
export function snapPalette(data) {
  for (let i = 0; i < data.length; i += 4) {
    let best = 0, bd = Infinity;
    for (let k = 0; k < CARD_RGB.length; k++) {
      const c = CARD_RGB[k], d = (data[i] - c[0]) ** 2 + (data[i + 1] - c[1]) ** 2 + (data[i + 2] - c[2]) ** 2;
      if (d < bd) { bd = d; best = k; }
    }
    [data[i], data[i + 1], data[i + 2]] = CARD_RGB[best]; data[i + 3] = 255;
  }
  return data;
}

// Draw a plan into a canvas (sets its size). Returns the layout. canvas: a DOM canvas or any object with width, height, getContext.
export function drawShareCard(canvas, plan, size) {
  canvas.width = size.w; canvas.height = size.h;
  const g = canvas.getContext('2d');
  const lay = layoutShareCard(plan, size, (t, px, bold) => { g.font = fontOf(px, bold); return g.measureText(t).width; });
  for (const r of lay.rects) { g.fillStyle = r.fill; g.fillRect(r.x, r.y, r.w, r.h); }
  drawTexts(g, lay, size);
  return lay;
}

// The bright card: the frame picture first (image: the loaded B2 frame, drawn 1:1 with no smoothing), then the slip, then the words.
export function drawBrightCard(canvas, plan, size, image, sheet = null) {
  canvas.width = size.w; canvas.height = size.h;
  const g = canvas.getContext('2d');
  const lay = layoutBrightCard(plan, size, (t, px, bold) => { g.font = fontOf(px, bold); return g.measureText(t).width; });
  g.imageSmoothingEnabled = false;
  g.drawImage(image, 0, 0, size.w, size.h);
  if (sheet) for (const i of lay.icons) g.drawImage(sheet, i.sx, i.sy, i.sw, i.sh, i.x, i.y, i.sw, i.sh); // the artist's icons, 1:1
  for (const r of lay.rects) { g.fillStyle = r.fill; g.fillRect(r.x, r.y, r.w, r.h); }
  drawTexts(g, lay, size);
  return lay;
}

function drawTexts(g, lay, size) {
  g.textBaseline = 'alphabetic';
  for (const t of lay.texts) {
    g.font = t.font; g.fillStyle = t.fill; g.textAlign = t.align;
    if (!g.getImageData) { g.fillText(t.text, t.x, t.y); continue; }
    const tw = Math.ceil(g.measureText(t.text).width), left = t.align === 'center' ? t.x - tw / 2 : t.x;
    const x0 = Math.max(0, Math.floor(left) - 6), y0 = Math.max(0, Math.floor(t.y - t.px)), x1 = Math.min(size.w, Math.ceil(left + tw) + 6), y1 = Math.min(size.h, Math.ceil(t.y + t.px * 0.45));
    const before = g.getImageData(x0, y0, x1 - x0, y1 - y0);
    g.fillText(t.text, t.x, t.y);
    const after = g.getImageData(x0, y0, x1 - x0, y1 - y0);
    thresholdRegion(before.data, after.data, rgbOf(t.fill), t.bold ? 0.5 : 0.25);
    g.putImageData(after, x0, y0);
  }
}
